import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { getBrandId } from '../../helpers/getBrandId/getBrandId';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { QueryKeys, SYNC_STATUS_QUERY } from '../QueryKeys';
import {
  EProductLibraryStatus,
  getProductLibrarySyncStatus,
  IStatus,
} from '../../api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { useState } from 'react';
import { triggerProductSync } from 'src/api/triggerProductSync/triggerProductSync';

export interface IProductLibraryStatus extends IStatus {
  callCount: number;
}

interface UseProductLibraryStatusOptions {
  onSyncStatusUpdate?: (data: IProductLibraryStatus) => void;
  queryOptions?: UseQueryOptions<IProductLibraryStatus>;
  enabled?: boolean;
}

export const useProductLibraryStatus = ({
  onSyncStatusUpdate,
  queryOptions,
  enabled = true,
}: UseProductLibraryStatusOptions = {}) => {
  const [callCount, setCallCount] = useState<number>(1);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const syncQueryKey = [getBrandId(), QueryKeys.librarySyncStatus];

  const mutation = useMutation({
    mutationFn: async () => {
      return triggerProductSync(true);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: syncQueryKey });

      queryClient.setQueryData(SYNC_STATUS_QUERY, (old: IProductLibraryStatus) => ({
        ...old,
        sync_status: EProductLibraryStatus.PENDING,
      }));
      showSnackbar({
        status: SnackbarStatus.PROGRESS,
        message: 'Product library sync started',
      });
    },
    onError: (error: unknown) => {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Failed to trigger sync',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_QUERY });
    },
  });

  const query = useQuery({
    enabled,
    queryKey: SYNC_STATUS_QUERY,
    queryFn: async () => {
      const status = await getProductLibrarySyncStatus();
      return {
        callCount,
        ...status,
      };
    },
    onError: () =>
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Unable to fetch product library sync status' }),
    refetchInterval: (data) => (data?.sync_status === EProductLibraryStatus.PENDING ? 10 * 1000 : false),
    onSuccess: (data) => {
      setCallCount((prev) => prev + 1);
      if (onSyncStatusUpdate) onSyncStatusUpdate(data);
    },
    ...queryOptions,
  });

  return {
    isSyncInProgress: query.data?.sync_status === EProductLibraryStatus.PENDING,
    triggerSync: mutation.mutate,
    syncInfo: query.data,
  };
};
